import { useCallback } from 'react';
import {
  Alert,
  Button,
  CancelIcon,
  Card,
  CheckCircleIcon,
  CheckIcon,
  DeleteIcon,
  Spinner,
  Text,
  Tooltip,
} from '@billda/design-system';
import { format, parseISO } from 'date-fns';
import { CondensedPotSummary } from '../CondensedPotSummary';
import { useApprovePot } from '../../hooks/useApprovePot';
import {
  useResolveRejectionReason,
  useRemoveRejectionReason,
} from '../../hooks/useRejectionReasons';
import type { PotDetailsDisplayModel } from '../../types/potDetails.types';

interface PotApprovalPanelContentProps {
  potDetails: PotDetailsDisplayModel;
  onSuccess: () => void;
  onReject?: () => void;
}

export function PotApprovalPanelContent({
  potDetails,
  onSuccess,
  onReject,
}: PotApprovalPanelContentProps) {
  const { mutate: approvePot, isPending: isApproving } = useApprovePot();

  const { mutate: resolveReason, isPending: isResolving } =
    useResolveRejectionReason();

  const { mutate: removeReason, isPending: isRemoving } =
    useRemoveRejectionReason();

  const isPending = isApproving || isResolving || isRemoving;

  const handleConfirmApproval = () => {
    approvePot(
      {
        potPeriodId: potDetails.potPeriodId,
        potId: potDetails.id,
      },
      {
        onSuccess: () => onSuccess(),
      }
    );
  };

  const handleResolveReason = useCallback(
    (reasonId: string) => {
      resolveReason({ potId: potDetails.id, reasonId });
    },
    [resolveReason, potDetails.id]
  );

  const handleRemoveReason = useCallback(
    (reasonId: string) => {
      removeReason({ potId: potDetails.id, reasonId });
    },
    [removeReason, potDetails.id]
  );

  // Get rejection reasons by status
  const unresolvedRejections = potDetails.rejectionReasons.filter(
    r => !r.isResolved
  );
  const resolvedRejections = potDetails.rejectionReasons.filter(
    r => r.isResolved
  );
  const hasOutstandingRejections =
    potDetails.approvals.hasOutstandingRejections;

  const formatDateTime = (dateString: string) => {
    return format(parseISO(dateString), 'dd MMM yyyy, HH:mm');
  };

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="flex flex-col gap-6">
          {/* Rejection Reasons Card - show if there are unresolved rejections */}
          {unresolvedRejections.length > 0 && (
            <Card className="border-border-error-strong p-4">
              <Text variant="h6" className="mb-2 text-content-error">
                Outstanding Rejection Reasons ({unresolvedRejections.length})
              </Text>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {unresolvedRejections.map(reason => (
                  <li
                    key={reason.id}
                    className="flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0">
                      <Text variant="body2">{reason.reason}</Text>
                      <Text variant="caption" color="secondary">
                        Added by {reason.createdByName}
                      </Text>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Tooltip content="Mark as resolved">
                        <span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 text-content-success"
                            onClick={() => {
                              handleResolveReason(reason.id);
                            }}
                            disabled={isPending}
                            data-testid="approval-resolve-reason-button"
                          >
                            <CheckIcon aria-hidden="true" className="size-4" />
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip content="Remove">
                        <span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 text-content-error"
                            onClick={() => {
                              handleRemoveReason(reason.id);
                            }}
                            disabled={isPending}
                            data-testid="approval-remove-reason-button"
                          >
                            <DeleteIcon aria-hidden="true" className="size-4" />
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Resolved Reasons Card */}
          {resolvedRejections.length > 0 && (
            <Card className="border-border-success-strong p-4">
              <Text variant="h6" color="secondary" className="mb-2">
                Resolved Reasons ({resolvedRejections.length})
              </Text>
              <ul className="m-0 flex list-none flex-col gap-2 rounded-lg bg-surface-raised p-2">
                {resolvedRejections.map(reason => (
                  <li key={reason.id} className="line-through opacity-70">
                    <Text variant="body2" className="line-through">
                      {reason.reason}
                    </Text>
                    <div className="flex flex-col">
                      <Text as="span" variant="caption" color="secondary">
                        Added by {reason.createdByName}
                      </Text>
                      {reason.resolvedAt && (
                        <Text
                          as="span"
                          variant="caption"
                          className="text-content-success"
                        >
                          Resolved by {reason.resolvedByName} on{' '}
                          {formatDateTime(reason.resolvedAt)}
                        </Text>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Condensed Pot Summary */}
          <CondensedPotSummary potDetails={potDetails} showDeductions={true} />
        </div>
      </div>

      {/* Fixed bottom action area */}
      <div className="border-t border-border-default bg-surface-default p-6">
        {/* Helper text */}
        {hasOutstandingRejections ? (
          <Alert severity="error" className="mb-4">
            Resolve or remove all outstanding rejection reasons before
            approving.
          </Alert>
        ) : (
          <Alert severity="info" className="mb-4">
            Approving this pot will send it for payment. This action cannot be
            undone.
          </Alert>
        )}

        <div className="flex flex-col gap-3">
          <Button
            variant="success"
            size="lg"
            fullWidth
            onClick={handleConfirmApproval}
            disabled={isPending || hasOutstandingRejections}
            data-testid="confirm-approval-button"
          >
            {isApproving ? (
              <Spinner size={20} color="inherit" />
            ) : (
              <CheckCircleIcon aria-hidden="true" className="size-5" />
            )}
            {isApproving ? 'Approving...' : 'Confirm Approval'}
          </Button>

          {onReject && (
            <Button
              variant="outlineDanger"
              size="lg"
              fullWidth
              onClick={onReject}
              disabled={isPending}
              data-testid="reject-pot-button"
            >
              <CancelIcon aria-hidden="true" className="size-5" />
              Reject Pot
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
